import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { APP_DRAWER_QUERY_KEY, ENTITY_ID_QUERY_KEY } from '@/utils/constants'

import {
  getNextProfileRouteByPathname,
  getProfileRouteByPathname,
  getProfileRouteBySearchParams
} from '../lib/utils'

export type UserProfileRoute = {
  screen: string
  entityId?: string
}
export type SetUserProfileRoute = (screen?: string, entityId?: string) => void
export type CloseUserProfile = () => void

type UseUserProfileRouteReturn = {
  userProfileRoute: UserProfileRoute | null
  setUserProfileRoute: SetUserProfileRoute
  closeUserProfile: CloseUserProfile
}

export const useUserProfileRoute = (): UseUserProfileRouteReturn => {
  const router = useRouter()
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()

  const userProfileRoute = useMemo(
    () =>
      pathname.includes('/profile')
        ? getProfileRouteByPathname(pathname)
        : getProfileRouteBySearchParams(searchParams),
    [pathname, searchParams]
  )

  const setUserProfileRoute = useCallback<SetUserProfileRoute>(
    (screen, entityId) => {
      const params = new URLSearchParams(searchParams?.toString())
      const userProfileRoute = pathname.includes('/profile')
        ? getNextProfileRouteByPathname(screen, entityId)
        : screen ?? ''

      params.set(APP_DRAWER_QUERY_KEY, userProfileRoute)
      if (entityId) {
        params.set(ENTITY_ID_QUERY_KEY, entityId)
      } else {
        params.delete(ENTITY_ID_QUERY_KEY)
      }
      router.push(`${pathname}?${params.toString()}`)
    },
    [pathname, router, searchParams]
  )

  const closeUserProfile = useCallback<CloseUserProfile>(() => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete(APP_DRAWER_QUERY_KEY)
    params.delete(ENTITY_ID_QUERY_KEY)
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname)
  }, [pathname, router, searchParams])

  return {
    userProfileRoute,
    setUserProfileRoute,
    closeUserProfile
  }
}
